import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { APIURL } from '../../../config';

const entityTypes = [
    { value: 'task_type', label: 'Task Types', singular: 'task type' },
    { value: 'employee', label: 'Employees', singular: 'employee' },
    { value: 'location', label: 'Locations', singular: 'location' }
];

const getLocationLabel = (location) => [
    location.loc_addr_line1,
    location.loc_addr_line2,
    location.loc_city,
    location.loc_state,
    location.loc_zip
].filter(Boolean).join(', ');

const getAssociationId = (association) =>
    association.association_id ?? association.program_association_id ?? association.id;

export default function Programs(props) {
    const [programs, setPrograms] = useState([]);
    const [associations, setAssociations] = useState([]);
    const [options, setOptions] = useState({
        task_type: [],
        employee: [],
        location: []
    });
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [selectedByType, setSelectedByType] = useState({
        task_type: [],
        employee: [],
        location: []
    });
    const [activeType, setActiveType] = useState('task_type');
    const [searchTerm, setSearchTerm] = useState('');
    const [newProgramName, setNewProgramName] = useState('');
    const [programNameError, setProgramNameError] = useState(false);
    const [programName, setProgramName] = useState('');
    const [programNameEditError, setProgramNameEditError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [changed, setChanged] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const loadProgramsData = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError('');

        try {
            const [programResponse, associationResponse, taskTypeResponse, employeeResponse, locationResponse] = await Promise.all([
                axios.get(`${APIURL}/programs`, { params: { company_id: props.comp_id } }),
                axios.get(`${APIURL}/programs/associations`, { params: { company_id: props.comp_id } }),
                axios.get(`${APIURL}/dropdowns/dropdownType/taskTypeList`, { params: { comp_id: props.comp_id } }),
                axios.get(`${APIURL}/dropdowns/dropdownType/employeeslist`, { params: { comp_id: props.comp_id } }),
                axios.get(`${APIURL}/location`, { params: { comp_id: props.comp_id } })
            ]);

            const programList = Array.isArray(programResponse.data) ? programResponse.data : [];
            setPrograms(programList);
            setAssociations(Array.isArray(associationResponse.data) ? associationResponse.data : []);
            setOptions({
                task_type: (Array.isArray(taskTypeResponse.data) ? taskTypeResponse.data : []).map(item => ({
                    id: String(item.ddID),
                    label: item.EntityValue || `Task Type #${item.ddID}`
                })),
                employee: (Array.isArray(employeeResponse.data) ? employeeResponse.data : []).map(item => ({
                    id: String(item.id ?? item.emp_id),
                    label: item.employee_name ||
                        `${item.emp_first_name || ''} ${item.emp_last_name || ''}`.trim() ||
                        `Employee #${item.id ?? item.emp_id}`
                })),
                location: (Array.isArray(locationResponse.data) ? locationResponse.data : []).map(item => ({
                    id: String(item.loc_id),
                    label: getLocationLabel(item) || item.loc_name || `Location #${item.loc_id}`
                }))
            });

            setSelectedProgram(current => {
                if (!current) return null;
                return programList.find(program =>
                    String(program.program_id) === String(current.program_id)
                ) || null;
            });
        } catch (err) {
            console.error(err);
            setError('Unable to load program settings.');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [props.comp_id]);

    useEffect(() => {
        loadProgramsData();
    }, [loadProgramsData]);

    useEffect(() => {
        const programId = selectedProgram?.program_id;
        const nextSelected = { task_type: [], employee: [], location: [] };

        if (programId != null) {
            associations
                .filter(association => String(association.program_id) === String(programId))
                .forEach(association => {
                    if (nextSelected[association.entity_type]) {
                        nextSelected[association.entity_type].push(String(association.entity_id));
                    }
                });
        }

        setSelectedByType(nextSelected);
        setChanged(false);
        setSearchTerm('');
    }, [associations, selectedProgram]);

    useEffect(() => {
        setProgramName(selectedProgram?.program_name || '');
        setProgramNameEditError(false);
    }, [selectedProgram]);

    const selectedIds = selectedByType[activeType];
    const filteredOptions = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        return options[activeType].filter(option =>
            !query || option.label.toLowerCase().includes(query)
        );
    }, [activeType, options, searchTerm]);
    const availableOptions = filteredOptions.filter(option => !selectedIds.includes(option.id));
    const assignedOptions = options[activeType].filter(option => selectedIds.includes(option.id));
    const activeTypeDetails = entityTypes.find(type => type.value === activeType);

    const updateSelected = (updater) => {
        setSelectedByType(current => ({
            ...current,
            [activeType]: updater(current[activeType])
        }));
        setChanged(true);
    };

    const toggleOption = (option) => {
        updateSelected(current =>
            current.includes(option.id)
                ? current.filter(id => id !== option.id)
                : [...current, option.id]
        );
    };

    const handleCreateProgram = async () => {
        const programName = newProgramName.trim();
        if (!programName) {
            setProgramNameError(true);
            setMessage('');
            setError('A program name is required.');
            return;
        }

        setSaving(true);
        setMessage('');
        setError('');
        setProgramNameError(false);

        try {
            const response = await axios.post(`${APIURL}/programs`, {
                program_name: programName,
                company_id: props.comp_id,
                created_by: props.user?.emp_id,
                updated_by: props.user?.emp_id
            });
            await loadProgramsData(false);
            const createdId = response.data?.id ?? response.data?.program_id;
            setSelectedProgram({
                program_id: createdId,
                program_name: programName
            });
            setNewProgramName('');
            setMessage('Program created successfully.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Unable to create program.');
        } finally {
            setSaving(false);
        }
    };

    const handleRenameProgram = async () => {
        const nextProgramName = programName.trim();
        if (!selectedProgram || saving) return;

        if (!nextProgramName) {
            setProgramNameEditError(true);
            setMessage('');
            setError('A program name is required.');
            return;
        }

        if (nextProgramName === (selectedProgram.program_name || '').trim()) return;

        setSaving(true);
        setMessage('');
        setError('');
        setProgramNameEditError(false);

        try {
            await axios.put(`${APIURL}/programs`, {
                program_id: Number(selectedProgram.program_id),
                program_name: nextProgramName,
                company_id: props.comp_id,
                created_by: selectedProgram.created_by ?? null,
                updated_by: props.user?.emp_id
            });

            await loadProgramsData(false);
            setSelectedProgram(current => current ? {
                ...current,
                program_name: nextProgramName
            } : current);
            setMessage('Program renamed successfully.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Unable to rename program.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAssociations = async () => {
        if (!selectedProgram || !changed || saving) return;

        setSaving(true);
        setMessage('');
        setError('');

        try {
            const programAssociations = associations.filter(association =>
                String(association.program_id) === String(selectedProgram.program_id)
            );
            const desiredKeys = new Set(
                entityTypes.flatMap(type =>
                    selectedByType[type.value].map(entityId => `${type.value}:${entityId}`)
                )
            );
            const existingKeys = new Set(
                programAssociations.map(association =>
                    `${association.entity_type}:${String(association.entity_id)}`
                )
            );
            const removed = programAssociations.filter(association =>
                !desiredKeys.has(`${association.entity_type}:${String(association.entity_id)}`)
            );
            const added = [...desiredKeys]
                .filter(key => !existingKeys.has(key))
                .map(key => {
                    const separator = key.indexOf(':');
                    return {
                        entity_type: key.slice(0, separator),
                        entity_id: key.slice(separator + 1)
                    };
                });

            await Promise.all([
                ...removed.map(association => {
                    const associationId = getAssociationId(association);
                    if (associationId == null) {
                        throw new Error('An existing association is missing its identifier.');
                    }
                    return axios.delete(`${APIURL}/programs/associations`, {
                        params: { program_association_id: associationId }
                    });
                }),
                ...added.map(association => axios.post(`${APIURL}/programs/associations`, {
                    program_id: Number(selectedProgram.program_id),
                    ...association
                }))
            ]);

            await loadProgramsData(false);
            setChanged(false);
            setMessage('Program associations saved successfully.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Unable to save program associations.');
        } finally {
            setSaving(false);
        }
    };

    const optionPanel = (title, panelOptions, assigned) => (
        <Paper variant="outlined" sx={{ width: '100%' }}>
            <Typography variant="h6" align="center" sx={{ py: 1 }}>{title}</Typography>
            <Divider />
            <List dense role="list" sx={{ overflow: 'auto', height: 270 }}>
                {panelOptions.map(option => (
                    <ListItem key={option.id} role="listitem" button onClick={() => toggleOption(option)}>
                        <ListItemIcon>
                            <Checkbox checked={assigned} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={option.label} />
                    </ListItem>
                ))}
                {panelOptions.length === 0 && (
                    <ListItem>
                        <ListItemText
                            primary={!assigned && searchTerm ? 'No matching items' : 'No items'}
                            primaryTypographyProps={{ color: 'text.secondary', align: 'center' }}
                        />
                    </ListItem>
                )}
            </List>
        </Paper>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5">Programs</Typography>
                    <Typography variant="body1" sx={{ color: 'gray' }}>
                        Create programs and manage their associations.
                    </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 3, backgroundColor: '#fafafa' }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h6">Create a New Program</Typography>
                            <Typography variant="body2" sx={{ color: 'gray' }}>
                                Give the program a clear name. You can configure its associations after it is created.
                            </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                            <TextField
                                fullWidth
                                required
                                label="New Program Name"
                                value={newProgramName}
                                error={programNameError}
                                helperText={programNameError ? 'Program name is required' : ''}
                                inputProps={{ maxLength: 255 }}
                                onChange={(event) => {
                                    setNewProgramName(event.target.value);
                                    if (event.target.value.trim()) setProgramNameError(false);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleCreateProgram();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleCreateProgram}
                                disabled={saving}
                                sx={{ minWidth: 160, height: 56 }}
                            >
                                Create Program
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h6">Manage a Program</Typography>
                            <Typography variant="body2" sx={{ color: 'gray' }}>
                                Select a program to rename it or change its current associations.
                            </Typography>
                        </Box>
                        <Divider />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Autocomplete
                                options={programs}
                                value={selectedProgram}
                                getOptionLabel={program =>
                                    program.program_name || `Program #${program.program_id}`
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option.program_id) === String(value.program_id)
                                }
                                onChange={(event, program) => setSelectedProgram(program)}
                                renderInput={(params) => <TextField {...params} label="Select Program" />}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Program Name"
                                value={programName}
                                error={programNameEditError}
                                helperText={programNameEditError ? 'Program name is required' : ''}
                                inputProps={{ maxLength: 255 }}
                                onChange={(event) => {
                                    setProgramName(event.target.value);
                                    if (event.target.value.trim()) setProgramNameEditError(false);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleRenameProgram();
                                    }
                                }}
                                disabled={!selectedProgram || saving}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleRenameProgram}
                                disabled={
                                    !selectedProgram ||
                                    saving ||
                                    !programName.trim() ||
                                    programName.trim() === (selectedProgram?.program_name || '').trim()
                                }
                                sx={{ minWidth: 150, height: 56 }}
                            >
                                Rename Program
                            </Button>
                        </Stack>
                        <Typography variant="body1" align="center">
                            {selectedProgram
                                ? `Managing associations for ${selectedProgram.program_name || `Program #${selectedProgram.program_id}`}`
                                : 'Select a program to manage its associations'}
                        </Typography>

                        <Tabs
                            value={activeType}
                            onChange={(event, value) => {
                                setActiveType(value);
                                setSearchTerm('');
                            }}
                            variant="fullWidth"
                        >
                            {entityTypes.map(type => (
                                <Tab
                                    key={type.value}
                                    value={type.value}
                                    label={`${type.label} (${selectedByType[type.value].length})`}
                                    disabled={!selectedProgram}
                                />
                            ))}
                        </Tabs>

                        <TextField
                            fullWidth
                            label="Search Available Associations"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            disabled={!selectedProgram}
                        />

                        <Grid container spacing={2} justifyContent="center" alignItems="center">
                            <Grid size={{ xs: 12, md: 5 }}>
                                {optionPanel(`Available ${activeTypeDetails.label}`, availableOptions, false)}
                            </Grid>
                            <Grid size={{ xs: 12, md: 2 }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'row', md: 'column' },
                                    gap: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => updateSelected(current => [
                                            ...current,
                                            ...availableOptions.map(option => option.id)
                                        ])}
                                        disabled={!selectedProgram || availableOptions.length === 0}
                                        aria-label={`Assign all visible ${activeTypeDetails.label}`}
                                    >
                                        {`Add all ${activeTypeDetails.label}`}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            const visibleIds = new Set(assignedOptions.map(option => option.id));
                                            updateSelected(current => current.filter(id => !visibleIds.has(id)));
                                        }}
                                        disabled={!selectedProgram || assignedOptions.length === 0}
                                        aria-label={`Remove all visible ${activeTypeDetails.label}`}
                                    >
                                        {`Remove all ${activeTypeDetails.label}`}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 5 }}>
                                {optionPanel(`Assigned ${activeTypeDetails.label}`, assignedOptions, true)}
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveAssociations}
                                disabled={!selectedProgram || !changed || saving}
                            >
                                {saving ? 'Saving...' : 'Save Associations'}
                            </Button>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>
            <Snackbar
                open={Boolean(error || message)}
                autoHideDuration={4000}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setMessage('');
                    setError('');
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={error ? 'error' : 'success'}
                    variant="filled"
                    onClose={() => {
                        setMessage('');
                        setError('');
                    }}
                    sx={{ width: '100%' }}
                >
                    {error || message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
